/**
 * Sampled browser-side diagnostics (plans/auth-embed-post-deploy-optimizations.md).
 *
 * The server cannot see a lazy Stories bundle that failed to load, Prebid
 * falling back, or ads starting at the auth deadline. One page in
 * SAMPLE_RATE reports which of these happened, as event codes only (no ids,
 * URLs or text), to the auth host when the page is hidden. The `page` event
 * is the denominator. Counted in server/src/services/sdkDiagnostics.js.
 * @module lib/sdkDiagnostics
 */

const { adBucket } = require('./adExperiments');

const SAMPLE_RATE = 0.05;
const ENDPOINT = '/api/sdk/diagnostics';
const EVENTS = new Set([
  'page',
  'stories_player',
  'stories_preroll',
  'stories_bundle_failed',
  'stories_init_failed',
  'prebid_unavailable',
  'prebid_timeout',
  'ads_error',
  'ads_auth_deadline'
]);

const state = { sampled: null, device: null, pageType: null, pending: new Set(), sent: new Set(), listening: false };

function issuer() {
  const value = typeof window !== 'undefined' ? window.MillaAuth?.getConfig?.()?.issuer : null;
  return value ? String(value).replace(/\/$/, '') : null;
}

function flush() {
  if (!state.pending.size) return;
  const base = issuer();
  if (!base || typeof fetch !== 'function') return;
  const events = [...state.pending];
  state.pending.clear();
  events.forEach((event) => state.sent.add(event));
  // A text/plain POST without credentials is a CORS simple request: no
  // preflight, no cookies. The answer is never read.
  fetch(`${base}${ENDPOINT}`, {
    method: 'POST',
    mode: 'no-cors',
    credentials: 'omit',
    keepalive: true,
    headers: { 'Content-Type': 'text/plain' },
    // `b`: the ad experiment bucket (0–9), so holdout pages count apart.
    body: JSON.stringify({ v: 1, d: state.device, p: state.pageType, b: adBucket(), e: events })
  }).catch(() => {});
}

function listen() {
  if (state.listening || typeof window === 'undefined' || typeof document === 'undefined') return;
  state.listening = true;
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

/**
 * Decides once per page whether it reports, from the ads init context.
 * @param {{ device?: string, pageType?: string, sampleRate?: number }} context
 */
function configureSdkDiagnostics({ device = null, pageType = null, sampleRate = SAMPLE_RATE } = {}) {
  if (state.sampled !== null) return state.sampled;
  state.sampled = Math.random() < sampleRate;
  state.device = device;
  state.pageType = pageType;
  if (state.sampled) reportSdkDiagnostic('page');
  return state.sampled;
}

/** Counts `event` once for this page when the page is sampled. */
function reportSdkDiagnostic(event) {
  if (!state.sampled || !EVENTS.has(event) || state.sent.has(event)) return;
  state.pending.add(event);
  listen();
}

module.exports = {
  configureSdkDiagnostics,
  reportSdkDiagnostic,
  flushSdkDiagnostics: flush,
  SDK_DIAGNOSTIC_EVENTS: EVENTS
};
